import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Megaphone, 
  Sparkles, 
  ChevronRight, 
  X, 
  Zap, 
  ShieldCheck, 
  Clock 
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const PlatformUpdates = () => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('updates'); // 'updates' or 'roadmap'

  // Fetch updates
  const { data: updates, isLoading, refetch } = useQuery('platform-updates', () => 
    axios.get('/api/updates').then(res => res.data.data),
    {
      refetchOnWindowFocus: true,
      staleTime: 60000, // 1 minute base stale time, but SSE will trigger instant refetches
    }
  );

  // Server-Sent Events (SSE) for Real-Time Auto-Refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    // Connect to the live stream
    const eventSource = new EventSource(`${axios.defaults.baseURL || ''}/api/updates/stream`);

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'new_update') {
          // A new update was published! Refetch the data silently in the background
          refetch();
          
          // Optionally, if the panel isn't open, we can show a hot-toast notification
          if (!isOpen && payload.data?.title) {
             toast.success(`New Update: ${payload.data.title}`, {
               icon: '🚀',
               duration: 5000,
               position: 'bottom-right',
             });
          }
        }
      } catch (err) {
        // Ignore JSON parse errors for pings
      }
    };

    return () => {
      eventSource.close();
    };
  }, [refetch, isOpen, isAuthenticated]);

  // Check for new updates since last viewed
  useEffect(() => {
    if (updates && updates.length > 0) {
      const lastSeen = localStorage.getItem('last_seen_update');
      const latestUpdate = updates[0]._id;
      
      if (lastSeen !== latestUpdate) {
        setHasNew(true);
      }
    }
  }, [updates]);

  const handleOpen = () => {
    setIsOpen(true);
    setHasNew(false);
    if (updates && updates.length > 0) {
      localStorage.setItem('last_seen_update', updates[0]._id);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (isLoading || !updates || updates.length === 0) return null;
  
  // Hide the button entirely if there are no new updates and the panel isn't already open
  if (!hasNew && !isOpen) return null;

  const latestVersion = updates[0]?.version || '1.0.4';

  return (
    <div className="relative">
      {/* Trigger Button (Epic Edge Style) */}
      <button
        onClick={handleOpen}
        className="relative group flex items-center gap-3 p-1.5 sm:px-4 sm:py-2.5 bg-slate-900 dark:bg-onyx-900 border border-slate-800 dark:border-onyx-800 rounded-xl sm:rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
      >
        <div className="relative">
          <div className="w-8 h-8 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:scale-110 transition-transform">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          {hasNew && (
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500 border-2 border-slate-900 dark:border-onyx-900"></span>
            </span>
          )}
        </div>
        
        <div className="text-left hidden sm:block">
          <p className="text-[10px] font-black text-white uppercase tracking-tight leading-none">Nexus Protocol</p>
          <p className="text-[8px] font-bold text-amber-500 uppercase tracking-[0.2em] leading-none mt-1">v{latestVersion}</p>
        </div>
      </button>

      {/* Sliding Panel / Modal Wrapper */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100000] flex justify-end overflow-hidden">
          <div 
            className="absolute inset-0 bg-slate-900/60 dark:bg-onyx-950/80 backdrop-blur-md transition-opacity duration-300" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="relative w-full max-w-full sm:max-w-sm bg-white dark:bg-onyx-900 h-full shadow-2xl border-l border-slate-200 dark:border-onyx-800 animate-in slide-in-from-right duration-500 flex flex-col">
            {/* Panel Header */}
            <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-onyx-800 flex items-center justify-between bg-white dark:bg-onyx-900 shrink-0">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center mr-3 shadow-lg shadow-amber-500/20">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none mb-1">
                    {showHistory ? 'Archive Link' : 'Latest Signal'}
                  </h3>
                  <p className="text-[9px] font-bold text-amber-500 uppercase tracking-widest leading-none">
                    {showHistory ? `${updates?.length || 0} Records Logged` : 'Active Transmission'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowHistory(!showHistory)}
                  className={`w-8 h-8 flex items-center justify-center border rounded-lg transition-all ${
                    showHistory 
                      ? 'bg-amber-500 text-white border-amber-600 shadow-md' 
                      : 'bg-slate-50 dark:bg-onyx-950 border-slate-100 dark:border-onyx-800 text-slate-400 hover:text-amber-500'
                  }`}
                  title={showHistory ? "Show Latest Only" : "View History Archive"}
                >
                  <Clock className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 flex items-center justify-center bg-slate-50 dark:bg-onyx-950 border border-slate-100 dark:border-onyx-800 text-slate-400 hover:text-rose-500 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Panel Tabs */}
            <div className="flex bg-slate-50 dark:bg-onyx-950 p-1 mx-6 mt-4 rounded-xl border border-slate-100 dark:border-onyx-800 shrink-0">
              <button 
                onClick={() => setActiveTab('updates')}
                className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                  activeTab === 'updates' 
                    ? 'bg-white dark:bg-onyx-900 text-amber-500 shadow-sm' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Announcements
              </button>
              <button 
                onClick={() => setActiveTab('roadmap')}
                className={`flex-1 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                  activeTab === 'roadmap' 
                    ? 'bg-white dark:bg-onyx-900 text-amber-500 shadow-sm' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Roadmap
              </button>
            </div>

            {/* Panel Content (Scrollable) */}
             <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5 custom-scrollbar bg-slate-50/30 dark:bg-onyx-950/20 min-h-0">
              {activeTab === 'updates' ? (
                updates && updates.length > 0 ? (
                  (showHistory ? updates : [updates[0]]).map((update, index) => (
                    <UpdateItem key={update._id || index} update={update} />
                  ))
                ) : (
                  <EmptyState refetch={refetch} />
                )
              ) : (
                <RoadmapMap updates={updates?.filter(u => u.type === 'roadmap') || []} />
              )}

              <div className="py-10 text-center shrink-0">
                <div className="w-1.5 h-1.5 bg-slate-300 dark:bg-onyx-800 rounded-full mx-auto mb-3"></div>
                <p className="text-[9px] font-black text-slate-400 dark:text-onyx-800 uppercase tracking-[0.4em]">Secure Feed Terminated</p>
              </div>
            </div>

            {/* Panel Footer */}
            <div className="p-4 sm:p-6 bg-white dark:bg-onyx-900 border-t border-slate-100 dark:border-onyx-800 shrink-0 pb-safe">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-onyx-950 rounded-2xl border border-slate-100 dark:border-onyx-850 shadow-inner">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3 shadow-md">
                    <Megaphone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-wider">EpicEdge Nexus</p>
                    <p className="text-[8px] font-bold text-amber-500 uppercase tracking-[0.2em] leading-none mt-1">v{latestVersion}</p>
                  </div>
                </div>
                <div className="px-2 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                   <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest leading-none">Operational</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlatformUpdates;

const UpdateItem = ({ update }) => {
  let releaseDateText;
  try { releaseDateText = update.releaseDate ? format(new Date(update.releaseDate), 'MMM d, yyyy') : 'Now'; } catch (e) { releaseDateText = 'Live'; }

  return (
    <div className="bg-white dark:bg-onyx-900 rounded-2xl p-5 border border-slate-200 dark:border-onyx-800 shadow-sm hover:border-amber-500/30 transition-all">
      <div className="flex items-center space-x-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
          update.type === 'feature' ? 'bg-blue-500 text-white' :
          update.type === 'fix' ? 'bg-emerald-500 text-white' :
          update.type === 'announcement' ? 'bg-purple-500 text-white' :
          'bg-slate-500 text-white'
        }`}>
          {update.type === 'feature' ? <Zap className="w-5 h-5" /> : 
           update.type === 'fix' ? <ShieldCheck className="w-5 h-5" /> : 
           <Megaphone className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <span className="block text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight">{update.title || 'Broadcast'}</span>
          <span className="block text-[9px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">
            {releaseDateText} • v{update.version || '1.0.0'}
          </span>
        </div>
      </div>
      <div className="bg-onyx-950 dark:bg-black rounded-xl p-4 border border-onyx-800 shadow-inner mb-4">
        <p className="text-[12px] text-gray-200 dark:text-white leading-relaxed font-bold">
          {update.description}
        </p>
      </div>
      <ReactionButtons 
        updateId={update._id} 
        initialReactions={update.reactions} 
        userReactions={update.userReactions}
      />
    </div>
  );
};

const ReactionButtons = ({ updateId, initialReactions, userReactions }) => {
  const { user, isAuthenticated } = useAuth();
  const [reactions, setReactions] = useState(initialReactions || {});
  const [userReaction, setUserReaction] = useState(null);

  const getDeviceId = () => {
    let id = localStorage.getItem('epic_device_id');
    if (!id) {
      id = `device_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
      localStorage.setItem('epic_device_id', id);
    }
    return id;
  };

  // Sync with global updates from server
  useEffect(() => {
    setReactions(initialReactions || {});
    const identifier = (isAuthenticated && user) ? user._id.toString() : getDeviceId();
    
    if (userReactions) {
      const myReaction = userReactions.find(r => r.user && r.user.toString() === identifier);
      setUserReaction(myReaction ? myReaction.type : null);
    }
  }, [initialReactions, userReactions, user, isAuthenticated]);

  const handleReact = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const deviceId = getDeviceId();
      
      const res = await axios.post(`/api/updates/${updateId}/react`, { type, deviceId }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data.success) {
        setReactions(res.data.data);
        setUserReaction(res.data.userReaction);
      }
    } catch (e) { 
      console.error('React failed', e);
      if (e.response?.status === 401) {
        toast.error('Session expired. Please re-authenticate.');
      }
    }
  };

  const reactionConfigs = [
    { type: 'thumbsup', emoji: '👍' },
    { type: 'heart', emoji: '❤️' },
    { type: 'rocket', emoji: '🚀' },
    { type: 'yay', emoji: '🎉' }
  ];

  return (
    <div className="flex gap-2 pt-2 overflow-x-auto pb-1 no-scrollbar">
      {reactionConfigs.map(config => (
        <button
          key={config.type}
          onClick={() => handleReact(config.type)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all text-xs font-black ${
            userReaction === config.type
              ? 'bg-amber-500/10 border-amber-500/50 text-amber-500'
              : 'bg-slate-50 dark:bg-onyx-950 border-slate-100 dark:border-onyx-800 text-slate-400 hover:border-amber-500/30'
          }`}
        >
          <span>{config.emoji}</span>
          <span className="text-[10px]">{reactions[config.type] || 0}</span>
        </button>
      ))}
    </div>
  );
};

const RoadmapMap = ({ updates }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const scrollContainerRef = useRef(null);
  
  // Calculate dynamic width: each node takes approx 200px
  const roadWidth = Math.max(1000, (updates.length + 1) * 200);

  return (
    <div className="relative py-6">
      {/* Horizontal Scroll Container - Constrained to parent width */}
      <div 
        ref={scrollContainerRef}
        className="overflow-x-auto no-scrollbar pb-12 cursor-grab active:cursor-grabbing scroll-smooth"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div 
          className="flex items-center relative h-[300px]"
          style={{ width: `${roadWidth}px`, minWidth: '100%' }}
        >
          
          {/* Horizontal Asphalt Road SVG - Dynamically Stretches */}
          {(() => {
            const segmentCount = Math.ceil(roadWidth / 200);
            const pathD = `M 0 32 ` + Array.from({ length: segmentCount }).map((_, i) => {
              const x1 = i * 200 + 100;
              const x2 = (i + 1) * 200;
              const y = i % 2 === 0 ? 0 : 64;
              return `Q ${x1} ${y} ${x2} 32`;
            }).join(' ');

            return (
              <div className="absolute inset-y-0 left-0 h-16 opacity-20 dark:opacity-40 pointer-events-none" style={{ width: roadWidth }}>
                <svg width={roadWidth} height="64" viewBox={`0 0 ${roadWidth} 64`} fill="none">
                   <path 
                     d={pathD} 
                     stroke="#1e293b" 
                     strokeWidth="40" 
                     strokeLinecap="round"
                     fill="none" 
                   />
                   <path 
                     d={pathD}
                     stroke="white" 
                     strokeWidth="2" 
                     strokeDasharray="12 15" 
                     fill="none" 
                   />
                </svg>
              </div>
            );
          })()}

          <div className="flex items-center w-full relative z-10">
            {updates.length > 0 ? (
              updates.map((item, index) => (
                <div 
                  key={item._id} 
                  className={`flex flex-col items-center transition-all duration-500 flex-shrink-0 ${selectedItem === item._id ? 'scale-110' : 'hover:scale-105'}`}
                  style={{ width: '200px' }}
                  onClick={() => setSelectedItem(selectedItem === item._id ? null : item._id)}
                >
                  {/* Connection Node (Pin) */}
                  <div className="relative w-14 h-14 shrink-0 cursor-pointer mb-4">
                    <div className={`absolute inset-0 rounded-full animate-pulse blur-lg opacity-30 ${
                      item.roadmapStatus === 'live' ? 'bg-emerald-500' :
                      item.roadmapStatus === 'in-progress' ? 'bg-blue-500' :
                      'bg-amber-500'
                    }`}></div>
                    
                    <svg viewBox="0 0 40 50" className={`w-full h-full drop-shadow-2xl transition-all ${
                       item.roadmapStatus === 'live' ? 'text-emerald-500' :
                       item.roadmapStatus === 'in-progress' ? 'text-blue-500' :
                       'text-amber-500'
                    }`}>
                      <path fill="currentColor" d="M20 0C11.1634 0 4 7.16344 4 16C4 28 20 50 20 50C20 50 36 28 36 16C36 7.16344 28.8366 0 20 0Z" />
                      <circle cx="20" cy="16" r="8" fill="white" />
                      <text x="20" y="20" textAnchor="middle" fontSize="11" fontWeight="900" fill="currentColor">{index + 1}</text>
                    </svg>
                  </div>

                  {/* Node Label */}
                  <div className="text-center">
                    <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${
                      item.roadmapStatus === 'live' ? 'text-emerald-500' :
                      item.roadmapStatus === 'in-progress' ? 'text-blue-500' :
                      'text-amber-500'
                    }`}>
                      {item.roadmapStatus}
                    </p>
                    <h5 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-tight truncate px-1">{item.title}</h5>
                  </div>

                  {/* Expanded Detail (Conditionally Rendered) - Positioned relative to node */}
                  {selectedItem === item._id && (
                    <div className="absolute top-[220px] w-64 bg-white dark:bg-onyx-900 rounded-2xl p-4 shadow-2xl border border-slate-100 dark:border-onyx-800 animate-in fade-in slide-in-from-top-4 duration-300 z-50">
                      <div className="flex justify-between items-start mb-3">
                         <div className="flex items-center gap-2">
                           <div className="w-6 h-6 bg-amber-500 rounded-lg flex items-center justify-center">
                              <Sparkles className="w-3.5 h-3.5 text-white" />
                           </div>
                           <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Phase Specification</p>
                         </div>
                         <button onClick={(e) => { e.stopPropagation(); setSelectedItem(null); }} className="text-slate-300 hover:text-rose-500">
                           <X className="w-3.5 h-3.5" />
                         </button>
                      </div>
                      <h4 className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2 text-left">{item.title}</h4>
                      <p className="text-[10px] text-slate-500 dark:text-gray-400 font-bold leading-relaxed mb-4 italic text-left">"{item.description}"</p>
                      <ReactionButtons updateId={item._id} initialReactions={item.reactions} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center w-full py-20 px-6">
                 <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Roadmap Initializing...</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Scroll Instruction */}
      <div className="flex items-center justify-center gap-2 opacity-50">
        <div className="h-px w-8 bg-slate-300 dark:bg-onyx-800"></div>
        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
          Scroll to explore <ChevronRight className="w-2.5 h-2.5" />
        </p>
        <div className="h-px w-8 bg-slate-300 dark:bg-onyx-800"></div>
      </div>
    </div>
  );
};

const EmptyState = ({ refetch }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center">
    <Megaphone className="w-12 h-12 text-amber-500/20 mb-4" />
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Searching for secure transmissions...</p>
    <button onClick={() => refetch()} className="mt-4 text-[9px] font-black text-amber-500 border border-amber-500/30 px-3 py-1 rounded-full hover:bg-amber-500/10 transition-all">Force Sync</button>
  </div>
);
